import {
    BoundingBox,
    Color,
    FlyController,
    Pose,
    Mat4,
    OrbitController,
    Vec2,
    Vec3,
    Quat
} from 'playcanvas';
import type { AppBase, Entity, EventHandler, GSplatComponent, InputController, Mouse} from 'playcanvas';

import { AnimController, AnimTrack } from './controllers/anim-controller';
import { easeOut } from './core/math';
import { AppController } from './input';
import { Picker } from './picker';
import { GizmoController } from './ui/GizmoController';
import { ViewCube } from './ui/view-cube';
import { RulerMarking } from './RulerMarking';

const pose = new Pose();

/**
 * Creates a rotation animation track
 *
 * @param initial - The initial pose of the camera.
 * @param keys - The number of keys in the animation.
 * @param duration - The duration of the animation in seconds.
 * @returns - The animation track object containing position and target keyframes.
 */
const createRotateTrack = (initial: Pose, keys: number = 12, duration: number = 20): AnimTrack => {
    const times = new Array(keys).fill(0).map((_, i) => i / keys * duration);
    const position: number[] = [];
    const target: number[] = [];

    const initialTarget = new Vec3();
    initial.getFocus(initialTarget);

    const mat = new Mat4();
    const vec = new Vec3();
    const dif = new Vec3(
        initial.position.x - initialTarget.x,
        initial.position.y - initialTarget.y,
        initial.position.z - initialTarget.z
    );

    for (let i = 0; i < keys; ++i) {
        mat.setFromEulerAngles(0, -i / keys * 360, 0);
        mat.transformPoint(dif, vec);

        position.push(initialTarget.x + vec.x);
        position.push(initialTarget.y + vec.y);
        position.push(initialTarget.z + vec.z);

        target.push(initialTarget.x);
        target.push(initialTarget.y);
        target.push(initialTarget.z);
    }

    return {
        name: 'rotate',
        duration,
        frameRate: 1,
        target: 'camera',
        loopMode: 'repeat',
        interpolation: 'spline',
        keyframes: {
            times,
            values: {
                position,
                target
            }
        }
    };
};

/**
 * Parses URL parameters and creates a swing (oscillating) or rotational animation track
 * for the camera around a given initial pose.
 *
 * URL parameter format example:
 *   http://example.com?axis=y&degrees=180&keys=24&duration=30
 *
 * Parameters:
 *   - axis: Rotation axis ('x', 'y', or 'z'). Default is 'y'.
 *   - degrees: Maximum swing angle in degrees. Default is 90.
 *   - keys: Number of keyframes used to build the animation curve. Default is 6.
 *   - duration: Total duration of the animation in seconds. Default is 12.
 *
 * Behavior:
 *   - Generates a set of keyframe positions by rotating the initial camera position
 *     around the chosen axis, centered on the initial target point.
 *   - The rotation angle is computed as a sinusoidal function, producing a smooth
 *     back-and-forth swing motion between -maxDegrees and +maxDegrees.
 *   - Both the camera position and its focus target are stored for each keyframe.
 *
 * @param initial - The initial camera pose (defines starting position and target).
 * @returns An animation track (AnimTrack) object that can be played in a loop.
 */
const createSwingTrackFromURL = (initial: Pose): AnimTrack => {
    const params = new URLSearchParams(window.location.search);

    const axis = (params.get('axis') || 'y') as 'x' | 'y' | 'z';
    const maxDegrees = parseFloat(params.get('degrees') || '90'); // URL 指定最大偏移角
    const keys = parseInt(params.get('keys') || '6', 10); // 幀數
    const duration = parseFloat(params.get('duration') || '12'); // 秒

    const times = new Array(keys).fill(0).map((_, i) => i / keys * duration);
    const position: number[] = [];
    const target: number[] = [];

    const initialTarget = new Vec3();
    initial.getFocus(initialTarget);

    const mat = new Mat4();
    const vec = new Vec3();
    const dif = new Vec3(
        initial.position.x - initialTarget.x,
        initial.position.y - initialTarget.y,
        initial.position.z - initialTarget.z
    );

    for (let i = 0; i < keys; ++i) {
        const t = i / (keys - 1);
        const angle = Math.sin(t * Math.PI * 2) * maxDegrees;

        switch (axis) {
            case 'x': mat.setFromEulerAngles(angle, 0, 0); break;
            case 'y': mat.setFromEulerAngles(0, angle, 0); break;
            case 'z': mat.setFromEulerAngles(0, 0, angle); break;
        }

        mat.transformPoint(dif, vec);

        position.push(initialTarget.x + 1.5 * vec.x);
        position.push(initialTarget.y + 1.5 * vec.y);
        position.push(initialTarget.z + 1.5 * vec.z);

        target.push(initialTarget.x);
        target.push(initialTarget.y);
        target.push(initialTarget.z);
    }

    return {
        name: 'swing',
        duration,
        frameRate: 1,
        target: 'camera',
        loopMode: 'repeat',
        interpolation: 'spline',
        keyframes: {
            times,
            values: {
                position,
                target
            }
        }
    };
};

class Viewer {
    app: AppBase;

    entity: Entity;

    events: EventHandler;

    state: any;

    settings: any;

    params: any;

    gizmoController: GizmoController | null = null;

    isGizmoActive: boolean = false;

    RulerMarking: RulerMarking;

    constructor(app: AppBase, entity: Entity, events: EventHandler, state: any, settings: any, params: any) {
        const { background, camera } = settings;
        const { graphicsDevice } = app;

        this.app = app;
        this.entity = entity;
        this.events = events;
        this.state = state;
        this.settings = settings;
        this.params = params;
        this.gizmoController = new GizmoController(app, entity);
        

        // disable auto render, we'll render only when camera changes
        app.autoRender = false;

        // apply camera animation settings
        entity.camera.clearColor = new Color(background.color);
        entity.camera.fov = camera.fov;

        // handle horizontal fov on canvas resize
        const updateHorizontalFov = () => {
            this.entity.camera.horizontalFov = graphicsDevice.width > graphicsDevice.height;
        };
        graphicsDevice.on('resizecanvas', () => {
            updateHorizontalFov();
            app.renderNextFrame = true;
        });
        updateHorizontalFov();

        // track camera changes
        const prevProj = new Mat4();
        const prevWorld = new Mat4();

        app.on('framerender', () => {
            const world = this.entity.getWorldTransform();
            const proj = this.entity.camera.projectionMatrix;
            const nearlyEquals = (a: Float32Array<ArrayBufferLike>, b: Float32Array<ArrayBufferLike>, epsilon = 1e-4) => {
                return !a.some((v, i) => Math.abs(v - b[i]) >= epsilon);
            };

            if (params.ministats) {
                app.renderNextFrame = true;
            }

            if (!app.autoRender && !app.renderNextFrame) {
                if (!nearlyEquals(world.data, prevWorld.data) ||
                    !nearlyEquals(proj.data, prevProj.data)) {
                    app.renderNextFrame = true;
                }
            }

            if (app.renderNextFrame) {
                prevWorld.copy(world);
                prevProj.copy(proj);
            }

            // suppress rendering till we're ready
            if (!state.readyToRender) {
                app.renderNextFrame = false;
            }
        });

        events.on('hqMode:changed', (value) => {
            graphicsDevice.maxPixelRatio = value ? window.devicePixelRatio : 1;
            app.renderNextFrame = true;
        });
        graphicsDevice.maxPixelRatio = state.hqMode ? window.devicePixelRatio : 1;
    }

    // initialize the viewer once gsplat asset is finished loading (so we know its bound etc)
    initialize() {
        const { app, entity, events, state, settings, params } = this;

        // get the gsplat
        const gsplat = app.root.findComponent('gsplat') as GSplatComponent;

        // calculate scene bounding box
        const bbox = gsplat?.instance?.meshInstance?.aabb ?? new BoundingBox();

        let cu = new Vec3(settings.camera.up);
        let cp = new Vec3(settings.camera.position);
        let cla = new Vec3(settings.camera.target);

        const quat = new Quat().setFromDirections(cu, new Vec3(0, 1, 0));
        const eulerangle = quat.getEulerAngles();

        cu = quat.transformVector(cu);
        cp = quat.transformVector(cp);
        cla = quat.transformVector(cla);
        
        if (gsplat && gsplat.entity) {
            gsplat.entity.rotate(eulerangle);
        } else {

        }

        // init ViewCube
        const viewCube = new ViewCube(events);
        const uiContainer = document.getElementById('ViewCubeContainer');
        if (uiContainer) {
            uiContainer.appendChild(viewCube.dom);
        }

        // create an anim camera
        // calculate the orbit camera frame position
        const framePose = (() => {
            const sceneSize = bbox.halfExtents.length();
            const distance = sceneSize / Math.sin(entity.camera.fov / 180 * Math.PI * 0.5);

            let position = new Vec3(2, 1, 2).normalize().mulScalar(distance).add(bbox.center);
            let target = bbox.center.clone();

            const isLargeScene = sceneSize > 10;

            if (isLargeScene) {
                position = cp.clone();
                target = cla.clone();
            }

            const url = new URL(window.location.href);
            const cpParam = url.searchParams.get("cp");
            const claParam = url.searchParams.get("cla");
            if (cpParam) {
                position = cp.clone();
            }
            if (claParam) {
                target = cla.clone();
            }

            return new Pose().look(position, target);
        })();

        // calculate the orbit camera reset position
        const resetPose = (() => {
            const { position, target, angles } = settings.camera;
            // return new Pose().look(
            return new Pose().look(cp, cla);
            //     new Vec3(position ?? [2, 1, 2]),
            //     new Vec3(target ?? [0, 0, 0])
            // );
        })();

        // calculate the user camera start position (the pose we'll use if there is no animation)
        //const useReset = settings.camera.position || settings.camera.target || bbox.halfExtents.length() > 100;
        
	const userStart = resetPose.clone();
	//const userStart = (useReset ? resetPose : framePose).clone();
        //const userStart = (useReset ? framePose : resetPose).clone();

        // if camera doesn't intersect the scene, assume it's an object we're
        // viewing
        const isObjectExperience = !bbox.containsPoint(userStart.position);

        // create the cameras
        const animCamera = ((initial, isObjectExperience) => {
            const { animTracks, camera } = settings;

            // extract the camera animation track from settings
            if (animTracks?.length > 0) {
                const track = animTracks.find((track: AnimTrack) => track.name);
                if (track) {
                    return AnimController.fromTrack(track);
                }
            } 
            else if (isObjectExperience) {

                // create basic rotation animation if no anim track is specified
                if (params.initSceneViewOption === 4) {
                    return AnimController.fromTrack(createRotateTrack(initial));
                } else if (params.initSceneViewOption === 0) {
                    return AnimController.fromTrack(createSwingTrackFromURL(initial));
                }
            }
            return null;
        })(userStart, isObjectExperience);
        const orbitCamera = (() => {
            const orbitCamera = new OrbitController();
            const originalUpdate = orbitCamera.update.bind(orbitCamera);
            const enablePan = params.enablepan;
            orbitCamera.update = (frame, dt) => {
                if (!enablePan) {
                    const originalRead = frame.read.bind(frame);
                    frame.read = () => {
                        const data = originalRead();
                        data.move[0] = 0;
                        data.move[1] = 0;
                        return data;
                    };
                }
                return originalUpdate(frame, dt);
            };
            orbitCamera.zoomRange = new Vec2(0.01, Infinity);
            if (params.ZoomInFactor !== undefined && params.ZoomOutFactor !== undefined) {
                orbitCamera.zoomRange = new Vec2(params.ZoomInFactor, params.ZoomOutFactor);
            } else if (params.ZoomInFactor !== undefined) {
                orbitCamera.zoomRange = new Vec2(params.ZoomInFactor, Infinity);
            } else if (params.ZoomOutFactor !== undefined) {
                orbitCamera.zoomRange = new Vec2(0.01, params.ZoomOutFactor);
            }
            // --- Pitch range (controlled via URL params) ---
            const pitchMin = params.pitchMin !== undefined ? params.pitchMin : -360; // Default: allow looking up to 90° upward
            const pitchMax = params.pitchMax !== undefined ? params.pitchMax : 360; // Default: allow looking down to 80°
            orbitCamera.pitchRange = new Vec2(pitchMin, pitchMax);

            // --- Yaw range (left/right rotation) controlled via URL ---
            const yawMin = params.yawMin !== undefined ? params.yawMin : -360; // max left
            const yawMax = params.yawMax !== undefined ? params.yawMax : 360;  // max right
            orbitCamera.yawRange = new Vec2(yawMin, yawMax);

            orbitCamera.rotateDamping = 0.97;
            orbitCamera.moveDamping = 0.97;
            orbitCamera.zoomDamping = 0.97;

            return orbitCamera;
        })();
        const flyCamera = (() => {
            const flyCamera = new FlyController();

            flyCamera.pitchRange = new Vec2(-90, 90);
            flyCamera.rotateDamping = 0.97;
            flyCamera.moveDamping = 0.97;

            return flyCamera;
        })();

        const getCamera = (cameraMode: 'orbit' | 'anim' | 'fly'): InputController => {
            switch (cameraMode) {
                case 'orbit': return orbitCamera;
                case 'anim': return animCamera;
                case 'fly': return flyCamera;
            }
        };

        // set the global animation flag
        state.hasAnimation = !!animCamera;
        state.animationDuration = animCamera ? animCamera.cursor.duration : 0;
        if (animCamera) {
            state.cameraMode = 'anim';
        }

        // this pose stores the current camera position. it will be blended/smoothed
        // toward the current active camera
        const activePose = new Pose();

        // create controller
        // set move speed based on scene size, within reason
        const controller = new AppController(app.graphicsDevice.canvas, entity.camera);
        controller.moveSpeed = bbox.halfExtents.length() * 0.1;

        // Keep track of the latest Gizmo rotation
        let gizmoQuat: Quat | null = null;
        let gizmoEuler: Vec3 | null = null;
        // Listen for rotation end events from the GizmoController and log the results
        app.on('gizmo:rotationEnd', (quat, euler) => {

            // Store the latest rotation for later use (e.g., in cameraInfo)
            gizmoQuat = quat.clone();
            gizmoEuler = euler.clone();

            // ui interface
            // const rotationDisplay = document.getElementById('rotation-display');
            // if (rotationDisplay) {
            //     rotationDisplay.innerText = `X: ${euler.x.toFixed(2)}, Y: ${euler.y.toFixed(2)}, Z: ${euler.z.toFixed(2)}`;
            // }
        });

        // Listen for events from the GizmoController
        app.on('gizmo:pointer', (isActive: boolean) => {
            this.isGizmoActive = isActive;

            if (!isActive) {
                // Reset the input controller’s frame to clear any accumulated mouse movement during Gizmo dragging.
                controller.update(0, state, activePose.distance);

                // Reattach the camera to synchronize its internal state and prevent jittering.。
                if (getCamera(state.cameraMode) === orbitCamera) {
                    orbitCamera.attach(activePose, false);
                }
            }
        });

        if (state.cameraMode === 'anim') {
            //  first frame of the animation
            activePose.copy(animCamera.update(controller.frame, 0));
        } else {
            // user start position
            activePose.copy(userStart);
        }

        // place all user cameras at the start position
        orbitCamera.attach(activePose, false);
        flyCamera.attach(activePose, false);

        // transition time between cameras
        let transitionTimer = 0;

        // the previous camera we're transitioning away from
        const prevPose = new Pose();
        let prevCamera: InputController | null = null;
        let prevCameraMode = 'orbit';

        // @ts-ignore
        const keyframes = {
            times: [],
            values: { position: [], target: [] }
        };
        let currentTime = 0;
        const frameStep = 2;

        const get_camera_info = () : string =>{

            const quatToUse = gizmoQuat ?? quat;

            const quat_invert = quatToUse.clone().invert();
            const pos = quat_invert.transformVector(activePose.position);
            const focus = quat_invert.transformVector(activePose.getFocus());
            const up = quat_invert.transformVector(new Vec3(0, 1, 0));

            // Helper function to round to 4 decimals (Invert x and y to match the mkk coordinate system)
            const fmt = (v: number) => v.toFixed(4);
            // If no Gizmo rotation, invert x and y
            const invertXY = gizmoQuat ? (v: number) => v : (v: number) => -v;
            const cla = `${fmt(invertXY(focus.x))},${fmt(invertXY(focus.y))},${fmt(focus.z)}`;
            const cp  = `${fmt(invertXY(pos.x))},${fmt(invertXY(pos.y))},${fmt(pos.z)}`;
            const cu  = `${fmt(invertXY(up.x))},${fmt(invertXY(up.y))},${fmt(up.z)}`;
            
            const urlParams = `&cla=${cla}&cp=${cp}&cu=${cu}`;
            console.log('--- Get Camera Info ---');
            console.log(urlParams);

            return urlParams;
        }

        // === [RulerMarking] ===
        this.RulerMarking = new RulerMarking(this.app, bbox, get_camera_info, eulerangle, gsplat.entity.getPosition(), quat); // [Future Work] remove gsplat position and eulerangle of old drawing URL
        this.RulerMarking.MouseClickEvent();

        const rulerMarkingEditButton = document.getElementById('ruler-marking-edit');

        let mouseDownPos: { x: number; y: number } | null = null;
        let isDragging = false;

        window.addEventListener('mousedown', (event) => {
            

            if (event.button !== 0 || event.target.closest('button, input, select, textarea, label, a, svg, [data-ui], .ui, .toolbar, .menu, .panel')) return;

            mouseDownPos = { x: event.clientX, y: event.clientY };
            isDragging = false;

            const onMouseMove = (moveEvent: MouseEvent) => {
                if (!mouseDownPos) return;
                const dx = moveEvent.clientX - mouseDownPos.x;
                const dy = moveEvent.clientY - mouseDownPos.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance > 5) {
                    isDragging = true;
                }
            };

            const onMouseUp = (upEvent: MouseEvent) => {

                if (upEvent.button === 0 && !isDragging && !this.RulerMarking.IsEnableRulerMarkingEdit) {

                    this.RulerMarking.IsEnableRulerMarking = !this.RulerMarking.IsEnableRulerMarking;

                    if (this.RulerMarking.IsEnableRulerMarking){
                        this.RulerMarking.EnableDrawLines();
                        rulerMarkingEditButton.style.display = 'block';
                    }
                    else{
                        this.RulerMarking.DisableDrawLines();
                        this.RulerMarking.IsEnableRulerMarkingEdit = false;
                        rulerMarkingEditButton.style.display = 'none';
                    }

                }

                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
                mouseDownPos = null;
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        });
        
        rulerMarkingEditButton?.addEventListener('click', () => {
            
            if(this.RulerMarking.IsEnableRulerMarking){
                
                this.RulerMarking.IsEnableRulerMarkingEdit = !this.RulerMarking.IsEnableRulerMarkingEdit;

                if(this.RulerMarking.IsEnableRulerMarkingEdit){
                    this.RulerMarking.EnableDrawLinesEdit();
                    
                }
                else{
                    this.RulerMarking.DisableDrawLinesEdit();
                }

            }
        });       
        // ===

        events.on('inputEvent', (eventName, event) => {
            const doReset = (pose: Pose) => {
                switch (state.cameraMode) {
                    case 'orbit': {
                        orbitCamera.attach(pose, true);
                        break;
                    }
                    case 'fly': {
                        if (state.cameraMode !== 'orbit') {
                            state.snap = true;
                            state.cameraMode = 'orbit';
                        }
                        orbitCamera.attach(pose, true);
                        break;
                    }
                    case 'anim': {
                        state.cameraMode = prevCameraMode;
                        break;
                    }
                }
            };

            switch (eventName) {
                case 'frame':
                    doReset(framePose);
                    break;

                case 'reset':
                    doReset(resetPose);
                    break;

                case 'cancel':
                case 'interrupt':
                    // When noui=1 and initSceneViewOption=4, ignore left-click interrupts while in anim mode
                    if (state.cameraMode === 'anim' && this.params && this.params.noui === 1 && this.params.initSceneViewOption === 4) {
                        const isLeftPointerDown = event && event.type === 'pointerdown' && event.button === 0;
                        if (isLeftPointerDown) {
                            break;
                        }
                    }
                    if (state.cameraMode === 'anim') {
                        state.cameraMode = prevCameraMode;
                    }
                    break;

                case 'cameraInfo': {
                    // Transform back to world space for url parameters ()
                    // Use the latest Gizmo rotation if available, otherwise fallback to default camera rotation
                    const urlParams = get_camera_info();

                    navigator.clipboard.writeText(urlParams)
                    .then(() => console.log('Camera info copied to clipboard!'))
                    .catch(err => console.error('Failed to copy camera info:', err));
                    break;
                }

                // ===== record Keyframe =====
                case 'recordKeyframe': {
                    const pos = activePose.position;
                    const focus = activePose.getFocus();
                    keyframes.times.push(currentTime);
                    keyframes.values.position.push(pos.x, pos.y, pos.z);
                    keyframes.values.target.push(focus.x, focus.y, focus.z);
                    currentTime += frameStep;
                    break;
                }

                // ===== export JSON =====
                case 'exportAnimTrack': {
                    const animTrack = {
                        name: 'camera_anim',
                        duration: currentTime,
                        frameRate: 1,
                        target: 'camera',
                        loopMode: 'repeat',
                        interpolation: 'spline',
                        keyframes
                    };
                    const sceneJson = { animTracks: [animTrack] };

                    const blob = new Blob([JSON.stringify(sceneJson, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);

                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'animTrack.json';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);

                    URL.revokeObjectURL(url);

                    break;
                }
            }
        });

        // application update
        app.on('update', (deltaTime) => {

            // Call the update method we just added in GizmoController every frame
            if (this.gizmoController) {
                this.gizmoController.update();
                if (this.gizmoController.gizmo) {
                    app.renderNextFrame = true;
                }
            }
            // Not sure if it should just be placed inside the object mode?
            const cameraMatrix = entity.getWorldTransform();
            viewCube.update(cameraMatrix);
            // in xr mode we leave the camera alone
            if (app.xr.active) {
                return;
            }
            // Update the controller only when the Gizmo is inactive to prevent accumulating mouse input during Gizmo dragging.
            if (!this.isGizmoActive) {
                controller.update(deltaTime, state, activePose.distance);
            }
            // If the Gizmo is active, we need continuous rendering to visualize the object's transformation.
            if (this.isGizmoActive) {
                app.renderNextFrame = true;
            }

            // update touch joystick UI
            if (state.cameraMode === 'fly') {
                events.fire('touchJoystickUpdate', controller.joystick.base, controller.joystick.stick);
            }

            // use dt of 0 if animation is paused
            const dt = state.cameraMode === 'anim' ?
                (state.animationPaused ? 0 : deltaTime * transitionTimer) :
                deltaTime;

            events.on('camera.align', (dir: string) => {
                const target = bbox.center.clone();

                const distance = activePose.position.clone().sub(target).length();
                const pos = new Vec3();

                switch (dir) {
                    case 'px': pos.set(target.x + distance, target.y, target.z); break;
                    case 'nx': pos.set(target.x - distance, target.y, target.z); break;
                    case 'py': pos.set(target.x, target.y + distance, target.z); break;
                    case 'ny': pos.set(target.x, target.y - distance, target.z); break;
                    case 'pz': pos.set(target.x, target.y, target.z + distance); break;
                    case 'nz': pos.set(target.x, target.y, target.z - distance); break;
                }

                const newPose = new Pose().look(pos, target);

                activePose.copy(newPose);
                orbitCamera.attach(activePose, true);

                entity.setPosition(activePose.position);
                entity.setEulerAngles(activePose.angles);

                app.renderNextFrame = true;
            });

            // Only update the camera's pose if the gizmo is not active
            if (!this.isGizmoActive) {
                pose.copy(getCamera(state.cameraMode).update(controller.frame, dt));

                if (transitionTimer < 1) {
                    transitionTimer = Math.min(1, transitionTimer + deltaTime);
                    if (transitionTimer < 1 && prevCamera) {
                        pose.lerp(prevPose, pose, easeOut(transitionTimer));
                    }
                }

                activePose.copy(pose);
                entity.setPosition(activePose.position);
                entity.setEulerAngles(activePose.angles);
            }

            // update animation timeline
            if (state.cameraMode === 'anim') {
                state.animationTime = animCamera.cursor.value;
            }
        });

        // handle camera mode switching
        events.on('cameraMode:changed', (value, prev) => {
            prevCameraMode = prev;
            prevPose.copy(activePose);
            prevCamera = getCamera(prev);
            prevCamera.detach();

            switch (value) {
                case 'orbit':
                case 'fly':
                    getCamera(value).attach(activePose, false);
                    break;
            }

            // reset camera transition timer
            if (!state.snap) {
                transitionTimer = 0;
            }
            state.snap = false;
        });

        events.on('setAnimationTime', (time) => {
            if (animCamera) {
                animCamera.cursor.value = time;

                // switch to animation camera if we're not already there
                if (state.cameraMode !== 'anim') {
                    state.cameraMode = 'anim';
                }
            }
        });

        // pick orbit camera focus point on double click
        let picker: Picker | null = null;
        events.on('inputEvent', async (eventName, event) => {
            switch (eventName) {
                case 'dblclick': {
                    if (!picker) {
                        picker = new Picker(app, entity);
                    }
                    const result = await picker.pick(event.offsetX, event.offsetY);
                    if (result) {
                        if (state.cameraMode !== 'orbit') {
                            state.snap = true;
                            state.cameraMode = 'orbit';
                        }

                        // snap distance of focus to picked point to interpolate rotation only
                        activePose.distance = activePose.position.distance(result);
                        orbitCamera.attach(activePose, false);

                        orbitCamera.attach(pose.look(activePose.position, result), true);
                    }
                    break;
                }
            }
        });

        // initialize the camera entity to initial position and kick off the
        // first scene sort (which usually happens during render)
        entity.setPosition(activePose.position);
        entity.setEulerAngles(activePose.angles);
        gsplat?.instance?.sort(entity);

        if (gsplat?.instance?.sorter) {
            // handle gsplat sort updates
            gsplat.instance.sorter.on('updated', () => {
                // request frame render when sorting changes
                app.renderNextFrame = true;
                if (!state.readyToRender) {
                    state.readyToRender = true;
                    // wait for the first valid frame to complete rendering
                    const frameHandle = app.on('frameend', () => {
                        frameHandle.off();
                        events.fire('firstFrame');
                        window.firstFrame?.();
                    });
                }
            });
        } else {

            state.readyToRender = true;
            events.fire('firstFrame');
            window.firstFrame?.();
        }
    }
}
export { Viewer };
